import React, { useMemo } from 'react';

const CelestialBackground: React.FC = () => {
  // Generate coordinates for an optimized star field once
  const celestialData = useMemo(() => {
    const createStar = (id: number, type: 'deep' | 'bright' | 'hero') => {
      // Use a wider range to prevent edges showing during rotation
      const x = Math.random() * 300 - 100; 
      const y = Math.random() * 300 - 100;
      
      let size = 1;
      let opacity = 0.5;
      let twinkle = 3;
      
      if (type === 'deep') {
        // Deep stars: Tiny but numerous
        size = Math.random() * 1.5 + 0.5;
        opacity = Math.random() * 0.3 + 0.1;
        twinkle = Math.random() * 10 + 5;
      } else if (type === 'bright') {
        // Bright stars: Noticeable glow and size
        size = Math.random() * 3.0 + 1.5;
        opacity = Math.random() * 0.5 + 0.3;
        twinkle = Math.random() * 8 + 4;
      } else if (type === 'hero') {
        // Hero stars: The "Jewels" of the sky
        size = Math.random() * 5.5 + 3.0;
        opacity = 0.8;
        twinkle = Math.random() * 9 + 6;
      }

      return {
        id,
        x,
        y,
        size,
        opacity,
        twinkle,
        hasSpark: type === 'hero' && Math.random() > 0.4,
      };
    };

    // Optimized star counts to prevent DOM overhead
    const deepStars = Array.from({ length: 400 }).map((_, i) => createStar(i, 'deep'));
    const brightStars = Array.from({ length: 150 }).map((_, i) => createStar(i + 400, 'bright'));
    const heroStars = Array.from({ length: 50 }).map((_, i) => createStar(i + 550, 'hero'));

    return { 
      stars: { deep: deepStars, bright: brightStars, hero: heroStars }
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-0 bg-[#010105] overflow-hidden">
      {/* Global Breathing Glow */}
      <div className="absolute inset-0 animate-sky-breath">
        {/* The Moving Celestial Sphere */}
        <div 
          className="absolute inset-[-150%] origin-center"
          style={{ animation: 'celestial-rotate 1500s linear infinite' }}
        >
          {/* Deep Field Stars */}
          {celestialData.stars.deep.map((star) => (
            <div
              key={star.id}
              className="absolute rounded-full bg-blue-100/40"
              style={{
                top: `${star.y}%`,
                left: `${star.x}%`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                opacity: star.opacity,
                animation: `celestial-twinkle ${star.twinkle}s ease-in-out infinite`,
              }}
            />
          ))}

          {/* Bright Stars */}
          {celestialData.stars.bright.map((star) => (
            <div
              key={star.id}
              className="absolute rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.4)]"
              style={{
                top: `${star.y}%`,
                left: `${star.x}%`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                opacity: star.opacity,
                animation: `celestial-twinkle ${star.twinkle}s ease-in-out infinite`,
              }}
            />
          ))}

          {/* Hero Stars */}
          {celestialData.stars.hero.map((star) => (
            <div
              key={star.id}
              className="absolute"
              style={{
                top: `${star.y}%`,
                left: `${star.x}%`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                animation: `celestial-pulse ${star.twinkle}s ease-in-out infinite`,
              }}
            >
              <div className="w-full h-full rounded-full bg-white shadow-[0_0_20px_rgba(255,255,255,0.8)]" />
              {star.hasSpark && (
                <>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600%] h-[1px] bg-white opacity-40 blur-[0.5px]" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600%] w-[1px] bg-white opacity-40 blur-[0.5px]" />
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Atmospheric Gradients */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#09040e] via-transparent to-transparent opacity-95" />
      <div className="absolute bottom-0 left-0 right-0 h-[70vh] bg-gradient-to-t from-amber-600/5 via-purple-950/10 to-transparent blur-[200px]" />

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes celestial-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes sky-breath {
          0%, 100% { opacity: 0.7; filter: brightness(0.9); }
          50% { opacity: 1; filter: brightness(1.2); }
        }
        @keyframes celestial-twinkle {
          0%, 100% { opacity: 0.2; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes celestial-pulse {
          0%, 100% { transform: scale(0.8); opacity: 0.5; }
          50% { transform: scale(1.2); opacity: 1; }
        }
        .animate-sky-breath {
          animation: sky-breath 30s ease-in-out infinite;
        }
      `}} />
    </div>
  );
};

export default CelestialBackground;